import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Campaign } from "@/types/campaign";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Target,
  Users,
  CheckCircle2,
  Clock,
  Briefcase,
  MapPin,
  Activity,
  Search,
  ArrowRight,
  LogOut,
  User,
  Settings,
  ChevronDown
} from "lucide-react";
import { format } from "date-fns";
import { CampaignStatusBadge } from "@/components/brand-dashboard/CampaignStatusBadge";
import { CampaignTimeline } from "@/components/brand-dashboard/CampaignTimeline";
import { useAuthStore } from "@/stores/authStore";

// Import Artie logo
import artieLogo from "@/assets/images/artie.png";

export default function BrowseEligibleProjectsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { brand, user, logout } = useAuthStore();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [eligibleProjectsCount, setEligibleProjectsCount] = useState<number | null>(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  // Update current time every minute for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileDropdownOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.profile-dropdown')) {
          setIsProfileDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  // Calculate countdown to booking window end
  const bookingCountdown = useMemo(() => {
    const windowDetails = campaign?.window_details;
    if (!windowDetails?.submission_end_date) {
      return null;
    }

    // Use backend's is_active as primary source of truth (checks both status AND dates)
    // This ensures UI matches actual system behavior
    console.log("🔍 FRONTEND BOOKING WINDOW DEBUG:", {
      windowName: windowDetails.name,
      status: windowDetails.status,
      is_active: windowDetails.is_active,
      submission_end_date: windowDetails.submission_end_date
    });
    
    if (windowDetails.is_active === false) {
      console.log("🔍 FRONTEND: Booking window marked as expired due to is_active=false");
      return { expired: true };
    }

    const endDate = new Date(windowDetails.submission_end_date);
    const now = currentTime;
    const timeDiff = endDate.getTime() - now.getTime();

    // Fallback: also check date in case is_active wasn't updated
    if (timeDiff <= 0) {
      return { expired: true };
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    return {
      expired: false,
      days,
      hours,
      minutes,
      timeString: `${days > 0 ? `${days} days ` : ''}${hours} hours`,
      formattedEndDate: format(endDate, "MMMM d h:mm a 'EST'")
    };
  }, [campaign?.window_details, currentTime]);

  useEffect(() => {
    async function loadCampaign() {
      if (!campaignId) return;
      
      try {
        const campaignData = await apiClient.getCampaign(campaignId);
        setCampaign(campaignData);
        
        // Try to get eligible projects count
        try {
          const projectsResponse = await apiClient.getCampaignEligibleProjects(campaignId, 1, 1000); // Request up to 1000 projects
          if (projectsResponse?.projects) {
            setEligibleProjectsCount(projectsResponse.projects.length);
          }
        } catch (projectsError) {
          console.log("Could not load projects count:", projectsError);
          // Don't set error state for this, just leave count as null
        }
      } catch (err) {
        console.error("Failed to load campaign", err);
        setError("Failed to load campaign details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }

    loadCampaign();
  }, [campaignId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-900 mx-auto mb-4"></div>
          <p className="text-neutral-500">Loading campaign details...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-neutral-50 p-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-neutral-900 mb-4">Error Loading Campaign</h1>
          <p className="text-red-600 mb-8">{error || "Campaign not found"}</p>
          <Button onClick={() => navigate("/brand-dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-geist">
      {/* Top Navigation */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo */}
            <Link to="/brand-dashboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={artieLogo} alt="Artie" className="w-10 h-10 mr-3" />
              <span className="font-work font-semibold text-3xl text-neutral-900 tracking-normal">artie</span>
            </Link>
            
            {/* Center - Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/brand-dashboard" className="text-neutral-600 hover:text-neutral-900 font-medium">
                Home
              </Link>
              <Link to="/campaigns" className="text-neutral-900 font-semibold">
                Campaigns
              </Link>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">IO & Payments</span>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">Settings</span>
            </nav>
            
            {/* Right side - User info */}
            <div className="flex items-center space-x-4">
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  <span className="bg-neutral-100 px-3 py-1 rounded-full font-medium">
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-neutral-100">
                        <p className="text-sm font-medium text-neutral-900">{user?.name || 'User'}</p>
                        <p className="text-xs text-neutral-500">{user?.email || 'user@example.com'}</p>
                      </div>
                      <div className="pt-2">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            logout();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link
          to="/brand-dashboard"
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          ALL CAMPAIGNS
        </Link>

        {/* Campaign Title */}
        <h1 className="text-4xl font-bold text-neutral-900 mb-12">
          {campaign.name || "EQS SUV Launch — Q3 2026"}
        </h1>

        {/* Campaign Details Section */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Total Budget */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">TOTAL BUDGET</h3>
              <div className="text-4xl font-bold text-neutral-900">
                ${campaign.requirements?.budget_usd?.toLocaleString() || "0"}
              </div>
            </div>

            {/* Tone */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">TONE</h3>
              <div className="flex flex-wrap gap-2">
                {campaign.requirements?.suitability_filters?.length > 0 ? (
                  campaign.requirements.suitability_filters.map((filter: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium"
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </span>
                  ))
                ) : (
                  <span className="text-neutral-500 text-sm italic">No tone filters specified</span>
                )}
              </div>
            </div>

            {/* Content Vertical */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">CONTENT VERTICAL</h3>
              <div className="flex flex-wrap gap-2">
                {campaign.requirements?.preferred_verticals?.length > 0 ? (
                  campaign.requirements.preferred_verticals.map((vertical: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-md text-sm font-medium"
                    >
                      {vertical.charAt(0).toUpperCase() + vertical.slice(1)}
                    </span>
                  ))
                ) : (
                  <span className="text-neutral-500 text-sm italic">No content verticals specified</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Progress */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-neutral-900 mb-12 text-center">Campaign Progress</h2>
          
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {/* Submit Requirements */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-900 text-sm">Submit</div>
                <div className="font-semibold text-neutral-900 text-sm">Requirements</div>
                <div className="text-xs text-neutral-500 mt-1">Completed</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-800 mx-4"></div>

            {/* Browse Eligible Projects */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mb-3">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div className="text-center">
                <div className="font-semibold text-neutral-900 text-sm">Browse Eligible</div>
                <div className="font-semibold text-neutral-900 text-sm">Projects</div>
                {campaign?.window_details?.is_active ? (
                  <div className="text-xs text-orange-600 mt-1 font-medium">● Active Now</div>
                ) : (
                  <div className="text-xs text-red-600 mt-1 font-medium">● Window Closed</div>
                )}
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-200 mx-4"></div>

            {/* Packages Composed */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-200 rounded-full flex items-center justify-center mb-3">
                <Briefcase className="w-6 h-6 text-neutral-400" />
              </div>
              <div className="text-center">
                <div className="font-medium text-neutral-500 text-sm">Packages</div>
                <div className="font-medium text-neutral-500 text-sm">Composed</div>
                <div className="text-xs text-neutral-400 mt-1">Pending</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-200 mx-4"></div>

            {/* Select & Confirm */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-200 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 className="w-6 h-6 text-neutral-400" />
              </div>
              <div className="text-center">
                <div className="font-medium text-neutral-500 text-sm">Select &</div>
                <div className="font-medium text-neutral-500 text-sm">Confirm</div>
                <div className="text-xs text-neutral-400 mt-1">Pending</div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="flex-1 h-0.5 bg-neutral-200 mx-4"></div>

            {/* Campaign Live */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-neutral-200 rounded-full flex items-center justify-center mb-3">
                <Activity className="w-6 h-6 text-neutral-400" />
              </div>
              <div className="text-center">
                <div className="font-medium text-neutral-500 text-sm">Campaign</div>
                <div className="font-medium text-neutral-500 text-sm">Live</div>
                <div className="text-xs text-neutral-400 mt-1">Pending</div>
              </div>
            </div>
          </div>
        </div>

        {/* Browse Eligible Projects Section */}
        <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mr-4">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">Browse Eligible Projects</h2>
              <p className="text-neutral-600 mt-1">
                Explore {eligibleProjectsCount || '24'} validated projects that match your campaign brief. Use preference signals to help tailor your package options.
              </p>
            </div>
          </div>

          {/* Booking Window Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-1">BOOKING WINDOW CLOSES IN</h3>
                <div className="text-2xl font-bold text-neutral-900 mb-1">
                  {bookingCountdown ? (
                    bookingCountdown.expired ? (
                      "Booking Window Closed"
                    ) : (
                      bookingCountdown.timeString || "4 days 12 hours"
                    )
                  ) : (
                    "4 days 12 hours"
                  )}
                </div>
                <div className="text-sm text-neutral-600 space-y-1">
                  {campaign?.window_details && (
                    <>
                      <div>
                        <strong>Period:</strong> {format(new Date(campaign.window_details.submission_start_date), "MMM d, yyyy 'at' h:mm a")} - {format(new Date(campaign.window_details.submission_end_date), "MMM d, yyyy 'at' h:mm a")}
                      </div>
                      <div>
                        <strong>Status:</strong> {campaign.window_details.status === 'closed' ? 'Closed by Admin' : 'Time Period Ended'}
                      </div>
                    </>
                  )}
                  {!campaign?.window_details && "Window Closed"}
                </div>
              </div>
              <div className="text-right text-sm text-neutral-600">
                <div>Packages are composed after the window closes.</div>
                <div>Browse eligible inventory and signal your preferences.</div>
              </div>
            </div>
          </div>

          {/* Preview Projects Button */}
            <Button
              className={`w-full py-4 text-lg font-semibold ${
                bookingCountdown?.expired
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-neutral-800 text-white hover:bg-neutral-900'
              }`}
              onClick={() => {
                if (!bookingCountdown?.expired) {
                  navigate(`/campaigns/${campaignId}/projects`);
                }
              }}
              disabled={bookingCountdown?.expired}
            >
              {bookingCountdown?.expired ? 'Booking Window Closed' : 'Preview Projects →'}
            </Button>
            {/* <Button
              className="w-full bg-blue-600 text-white hover:bg-blue-700 py-4 text-lg font-semibold"
              onClick={() => navigate(`/campaigns/${campaignId}/packages`)}
            >
              Browse Available Packages →
            </Button>
          </div> */}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-neutral-200 py-6 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <p className="text-sm text-neutral-500">© 2026 Artie. All rights reserved.</p>
            <div className="flex space-x-4">
              <a href="#" className="text-neutral-400 hover:text-neutral-600">
                <span className="sr-only">LinkedIn</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-neutral-400 hover:text-neutral-600">
                <span className="sr-only">Twitter</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}